/**
 * Türk kimlik/vergi numarası kontrol basamağı algoritmaları.
 *
 * İkisi de yalnızca numaranın kendi içinde tutarlı olduğunu söyler; numaranın
 * gerçekten var olduğunu doğrulamaz — bunun için GİB/NVİ tarafında kimlik
 * doğrulaması gereken (e-Devlet girişi ya da ücretli entegratör) servisler var.
 *
 * Kullanım farkı bilinçli:
 *   - TCKN sunucu tarafında da zorunlu (App\Rules\ValidTckn) — algoritma
 *     güvenilir, geçmeyen numara kaydedilmez.
 *   - VKN sunucu tarafında yalnızca "10 hane" olarak doğrulanır. Buradaki
 *     kontrol sadece kullanıcıya yumuşak bir uyarı göstermek için: istisnai
 *     bazı geçerli VKN'ler algoritmayı sağlamıyor ve lead formunda hatalı red
 *     doğrudan müşteri kaybı demek.
 */

/**
 * T.C. Kimlik No: 11 hane, ilk hane 0 olamaz.
 *   d10 = ((d1+d3+d5+d7+d9) * 7 - (d2+d4+d6+d8)) mod 10
 *   d11 = (d1..d10 toplamı) mod 10
 */
export function isValidTckn(value) {
  const tckn = String(value ?? '');

  if (!/^[1-9][0-9]{10}$/.test(tckn)) return false;

  const d = [...tckn].map(Number);
  const odd = d[0] + d[2] + d[4] + d[6] + d[8];
  const even = d[1] + d[3] + d[5] + d[7];

  // JS'in % operatörü de negatif kalan döndürür, sonuç 0-9'a normalize ediliyor.
  if (((((odd * 7) - even) % 10) + 10) % 10 !== d[9]) return false;

  return d.slice(0, 10).reduce((a, b) => a + b, 0) % 10 === d[10];
}

/**
 * Vergi Kimlik No: 10 hane, son hane kontrol basamağı.
 * i = 0..8 için tmp = (d[i] + (9 - i)) mod 10; tmp 0 ise 9, değilse
 * (tmp * 2^(9-i)) mod 9 (0 çıkarsa 9) toplanır; d[9] = (10 - toplam mod 10) mod 10.
 */
export function isValidVkn(value) {
  const vkn = String(value ?? '');

  if (!/^[0-9]{10}$/.test(vkn)) return false;

  const d = [...vkn].map(Number);
  let sum = 0;

  for (let i = 0; i < 9; i++) {
    const tmp = (d[i] + (9 - i)) % 10;

    if (tmp === 0) {
      sum += 9;
      continue;
    }

    const p = (tmp * 2 ** (9 - i)) % 9;
    sum += p === 0 ? 9 : p;
  }

  return (10 - (sum % 10)) % 10 === d[9];
}
